import {
  DeskPaymentIneligibilityReason,
  DeskPaymentStatus,
  InterventionEstimateSource,
  InterventionOutcome
} from './deskPayment.model';

/**
 * i18n keys for the desk-payment (card terminal) enums, shared by the dialog and the PDV
 * payment-state pills. Follows the {@link billingLineStatusLabelKey} precedent: every enum is
 * worded in one place, under one namespace.
 */
export const DESK_PAYMENT_KEY_PREFIX =
  'doctor-dashboard.billing.pdv.deskPayment';

/**
 * Why an amount-moving action is barred right now: a card payment holds the line, and
 * medical-synthesis would refuse it with `E1-12-2-1072`. Every surface that gates on the lock
 * says it with this one key — a tooltip, a menu item's caption, the collect form's warning.
 */
export const DESK_PAYMENT_LOCKED_KEY =
  `${DESK_PAYMENT_KEY_PREFIX}.locked` as const;

/**
 * The card-terminal control's own wording, per what it does to the line: start a payment, or
 * walk back to the one already live on a reader. Shared by the PDV row and the chase dashboard
 * cell, so the same button is never named two ways.
 */
export const DESK_PAYMENT_START_ACTION_KEY =
  `${DESK_PAYMENT_KEY_PREFIX}.action.start` as const;

export const DESK_PAYMENT_RESUME_ACTION_KEY =
  `${DESK_PAYMENT_KEY_PREFIX}.action.resume` as const;

/** The control that reports what became of the intervention behind a two-bite hold. */
export const DESK_PAYMENT_OUTCOME_ACTION_KEY =
  `${DESK_PAYMENT_KEY_PREFIX}.outcome.action` as const;

export const deskPaymentStatusLabelKey = (status: DeskPaymentStatus) =>
  `${DESK_PAYMENT_KEY_PREFIX}.status.${status}` as const;

export const deskPaymentIneligibilityLabelKey = (
  reason: DeskPaymentIneligibilityReason
) => `${DESK_PAYMENT_KEY_PREFIX}.ineligible.${reason}` as const;

/**
 * How the amounts were collected, for the audit trail's authorization row. Only the terminal
 * paths are worded ({@link PaymentPath} `TWO_BITE` / `SINGLE_BITE` / `ACTE_ONLY`) — the
 * payment-link paths belong to a later phase, so callers pass the raw token as the fallback,
 * as {@link billingLineEventTypeLabelKey} does.
 */
export const paymentPathLabelKey = (path: string) =>
  `${DESK_PAYMENT_KEY_PREFIX}.path.${path}` as const;

/** Names where the pre-filled intervention amount came from, under the amount field. */
export const interventionEstimateSourceHintKey = (
  source: InterventionEstimateSource
) => `${DESK_PAYMENT_KEY_PREFIX}.estimateSource.${source}` as const;

/**
 * Why a hold ended or was replaced — `details.reason` on STRIPE_RELEASED and the renewal-result
 * events (`INTERVENTION_CANCELED` / `NOTHING_TO_CAPTURE` / `CANCELED_ON_STRIPE` /
 * `HOLD_EXPIRED` / `RENEWED` / `RENEWAL_FAILED`; HOLD_EXPIRED is the sweeper's own ending when
 * a hold simply outlives `captureBefore` with nobody resolving it).
 * Raw token, as {@link paymentPathLabelKey}. Reached from
 * {@link billingLineEventReasonLabelKey}, which is what knows a `reason` detail is written in
 * this vocabulary rather than in the waive one.
 */
export const holdReleaseReasonLabelKey = (reason: string) =>
  `${DESK_PAYMENT_KEY_PREFIX}.releaseReason.${reason}` as const;

/**
 * Why the card was refunded — `details.reason` on STRIPE_REFUND_SYNCED, STRIPE_REFUND_FAILED
 * and STRIPE_LINK_PAYMENT_REFUNDED. Stripe's own vocabulary here (`requested_by_customer`,
 * `duplicate`, `fraudulent`, …), not ours, hence a namespace of its own rather than the waive
 * reasons the trail would otherwise fall through to.
 */
export const stripeRefundReasonLabelKey = (reason: string) =>
  `${DESK_PAYMENT_KEY_PREFIX}.refundReason.${reason}` as const;

/**
 * Why the cardholder disputed the charge — `details.reason` on STRIPE_DISPUTE_FLAGGED and
 * STRIPE_DISPUTE_CLOSED, Stripe's own vocabulary too.
 */
export const stripeDisputeReasonLabelKey = (reason: string) =>
  `${DESK_PAYMENT_KEY_PREFIX}.disputeReason.${reason}` as const;

/**
 * Where a refund or a dispute stands on Stripe's side — `details.stripeStatus` on all four
 * Stripe refund/dispute events (STRIPE_REFUND_SYNCED, STRIPE_REFUND_FAILED,
 * STRIPE_DISPUTE_FLAGGED, STRIPE_DISPUTE_CLOSED), named apart from `details.status` because
 * payment snapshots reuse that key. One namespace for both refunds and disputes: their token
 * sets are disjoint (`succeeded` / `pending` / `failed` against `needs_response` /
 * `under_review` / …), so a second builder would only duplicate this one.
 */
export const stripeStatusLabelKey = (status: string) =>
  `${DESK_PAYMENT_KEY_PREFIX}.stripeStatus.${status}` as const;

/**
 * Why the acte line was not exempted alongside a released hold — STRIPE_RELEASED's
 * `waiveSkipped`, in the audit trail. Takes the raw token, as {@link paymentPathLabelKey} does,
 * so a reason added backend-side degrades to its own name instead of breaking the build.
 */
export const waiveSkippedReasonLabelKey = (reason: string) =>
  `${DESK_PAYMENT_KEY_PREFIX}.waiveSkipped.${reason}` as const;

/** What the desk reports about the intervention, on the outcome dialog's two-choice control. */
export const interventionOutcomeLabelKey = (outcome: InterventionOutcome) =>
  `${DESK_PAYMENT_KEY_PREFIX}.outcome.${outcome}` as const;

/** Ordered choices of that control: the expected outcome first. */
export const INTERVENTION_OUTCOMES: readonly InterventionOutcome[] = [
  'OPERATED',
  'CANCELED'
];
